// Push subscription against the BlockCast worker — Web Push in browsers,
// APNs via the Capacitor plugin in the native iOS shell (nativePush.ts).
// The app is fully functional without the worker deployed — everything
// degrades to in-app alerts when push is unavailable.

import { Capacitor } from '@capacitor/core';
import type { ActivityId, Criteria, CustomActivity } from '../core/activities';
import type { PlannedSession } from '../core/alerts';
import type { Lang } from '../i18n';
import type { UnitSystem } from '../core/units';
import { resyncNativePush, subscribeNativePush, unsubscribeNativePush } from './nativePush';

const API = import.meta.env.VITE_PUSH_API as string | undefined;
const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PushAvailability = 'ok' | 'unsupported' | 'unconfigured' | 'denied';

export function pushAvailability(): PushAvailability {
  if (Capacitor.isNativePlatform()) {
    // native needs only the worker URL; permission denial surfaces when
    // subscribing (the OS prompt/settings own that state, not the page)
    return API ? 'ok' : 'unconfigured';
  }
  if (!API || !VAPID_PUBLIC) return 'unconfigured';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
}

function urlB64ToUint8Array(base64: string): Uint8Array {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export interface PushContext {
  sessions: PlannedSession[];
  /** Resolved criteria per activity present in sessions. */
  critFor: (id: ActivityId) => Criteria;
  /** User-created activities — their names travel with the subscription so
   * the worker can label push alerts. */
  customs?: readonly CustomActivity[];
  tolMult: number;
  lang: Lang;
  units: UnitSystem;
}

/** The transport-independent part of the subscribe body. */
export function buildSubscribeBody(ctx: PushContext): Record<string, unknown> {
  const criteria: Record<
    string,
    { w: Criteria['weights']; tMin: number; tMax: number; snowBase?: number }
  > = {};
  for (const s of ctx.sessions) {
    if (criteria[s.activityId]) continue;
    const c = ctx.critFor(s.activityId);
    criteria[s.activityId] = {
      w: c.weights,
      tMin: c.tMin,
      tMax: c.tMax,
      ...(c.act.snowBase != null ? { snowBase: c.act.snowBase } : {}),
    };
  }
  const customName = (id: ActivityId) => ctx.customs?.find((c) => c.id === id)?.name;
  return {
    sessions: ctx.sessions.map((s) => ({ ...s, name: customName(s.activityId) })),
    criteria,
    tolMult: ctx.tolMult,
    lang: ctx.lang,
    units: ctx.units,
  };
}

export async function subscribePush(ctx: PushContext): Promise<boolean> {
  if (Capacitor.isNativePlatform()) return subscribeNativePush(buildSubscribeBody(ctx));
  if (pushAvailability() !== 'ok') return false;
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC!).buffer as ArrayBuffer,
  });
  const res = await fetch(`${API}/api/subscribe`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ subscription: sub.toJSON(), ...buildSubscribeBody(ctx) }),
  });
  return res.ok;
}

export type ResyncResult = 'ok' | 'no-transport' | 'failed';

/** Mirror the planner to the worker after the user has opted in: same body
 * as subscribing, on the transport already registered, no prompt. With no
 * sessions left the subscription is deleted so nothing stale gets watched;
 * the next planned session re-creates it. */
export async function resyncPush(ctx: PushContext): Promise<ResyncResult> {
  const body = buildSubscribeBody(ctx);
  const hasSessions = ctx.sessions.length > 0;
  if (Capacitor.isNativePlatform()) return resyncNativePush(body, hasSessions);
  if (!API || !('serviceWorker' in navigator)) return 'no-transport';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return 'no-transport';
    const res = hasSessions
      ? await fetch(`${API}/api/subscribe`, {
          method: 'POST',
          keepalive: true,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ subscription: sub.toJSON(), ...body }),
        })
      : await fetch(`${API}/api/subscribe`, {
          method: 'DELETE',
          keepalive: true,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
    return res.ok ? 'ok' : 'failed';
  } catch {
    return 'failed';
  }
}

export async function unsubscribePush(): Promise<void> {
  if (Capacitor.isNativePlatform()) return unsubscribeNativePush();
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  if (API)
    await fetch(`${API}/api/subscribe`, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => {});
  await sub.unsubscribe();
}
